import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import { useAlert } from 'context/AlertContext';

import { DocumentIcon } from '@heroicons/react/16/solid';

import { isNameTakenError } from 'utils/nameTaken';
import { updatePromoter } from '../../../redux/actions/promoter';
import {
  validateFiles,
  combinedUploadError,
  uploadErrorMessage,
  IMAGE_OR_DOCUMENT_ACCEPT,
} from 'utils/uploadValidation';

const PromoterInfoEdit = ({ exitEditMode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const { promoter, loading } = useSelector((state) => state.promoter);

  const [formData, setFormData] = useState({
    username: promoter?.username || '',
    companyName: promoter?.companyName || '',
    headquarters: promoter?.headquarters || '',
    country: promoter?.country || '',
    city: promoter?.city || '',
    fiscalNumber: promoter?.fiscalNumber || '',
    firm: promoter?.firm || '',
    profileInfo: promoter?.profileInfo || '',
    iban: promoter?.iban || '',
    phoneNumber: promoter?.phoneNumber || '',
    websiteUrl: promoter?.websiteUrl || '',
  });

  const [saving, setSaving] = useState(false);
  const [documentProofFile, setDocumentProofFile] = useState(null);
  const [documentProofName, setDocumentProofName] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const showUploadErrors = (uploadErrors) => {
    showAlert({
      type: 'warning',
      title: t('upload.error_title', { defaultValue: 'Upload issue' }),
      message: uploadErrors.join('\n'),
      primaryAction: { label: t('common.ok') },
    });
  };

  const onDocumentChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { accepted, errors: uploadErrors } = validateFiles([file], {
      kind: 'imageOrDocument',
      t,
    });
    if (uploadErrors.length) {
      showUploadErrors(uploadErrors);
      e.target.value = '';
      return;
    }
    if (accepted.length) {
      setDocumentProofFile(file);
      setDocumentProofName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const combinedError = combinedUploadError([documentProofFile], t);
    if (combinedError) {
      showUploadErrors([combinedError]);
      return;
    }

    if (!formData.username.trim()) {
      showAlert({
        type: 'warning',
        title: t('alert_dialog.edit.update_error_title'),
        message: t('dashboard.name_required', 'Name cannot be empty'),
        primaryAction: { label: t('common.ok') },
      });
      return;
    }

    setSaving(true);
    try {
      const body = new FormData();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v ?? ''));
      body.set('username', formData.username.trim());
      if (documentProofFile) {
        body.append('documentProof', documentProofFile, documentProofName);
      }
      await dispatch(updatePromoter(body));

      showAlert({
        title: t('alert_dialog.edit.update_success_title'),
        message: t('alert_dialog.edit.update_success_message'),
        primaryAction: {
          label: t('alert_dialog.edit.update_success_primary_action'),
        },
      });
      exitEditMode();
    } catch (err) {
      // Stay in edit mode so the user's changes aren't lost.
      const uploadMessage = uploadErrorMessage(err, t);
      showAlert({
        title: t('alert_dialog.edit.update_error_title'),
        message: isNameTakenError(err)
          ? t('dashboard.name_taken')
          : uploadMessage || t('alert_dialog.edit.update_error_message'),
        primaryAction: {
          label: t('alert_dialog.edit.update_error_primary_action'),
        },
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !promoter) {
    return (
      <div className="flex justify-center py-10">
        <ClipLoader color="#FFBD59" size={50} />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full bg-white rounded-br-3xl shadow-lg p-6 sm:p-8 space-y-6"
    >
      {/* New Section: Top-level fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {[
          {
            name: 'username',
            label: t('dashboard.username', 'Username'),
          },
          { name: 'companyName', label: t('dashboard.company_name') },
          { name: 'headquarters', label: t('dashboard.headquarters') },
        ].map(({ name, label }) => (
          <div key={name} className="flex flex-col">
            <label className="text-sm font-semibold text-yellow-03 mb-1">
              {label}
            </label>
            <input
              type="text"
              name={name}
              value={formData[name]}
              onChange={handleChange}
              className="border rounded px-3 py-2 text-sm text-gray-700"
            />
          </div>
        ))}
      </div>

      {/* Core Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {[
          { name: 'country', label: t('dashboard.country') },
          { name: 'city', label: t('dashboard.city') },
          { name: 'fiscalNumber', label: t('dashboard.fiscal_number') },
          { name: 'firm', label: t('dashboard.firm') },
          { name: 'iban', label: t('dashboard.iban') || 'IBAN' },
          {
            name: 'phoneNumber',
            label: t('dashboard.phone_number', 'Phone number'),
          },
          {
            name: 'websiteUrl',
            label: t('dashboard.website', 'Website'),
          },
          // { name: 'email', label: t('dashboard.email') },
        ].map(({ name, label }) => (
          <div key={name} className="flex flex-col">
            <label className="text-sm font-semibold text-yellow-03 mb-1">
              {label}
            </label>
            <input
              type="text"
              name={name}
              value={formData[name]}
              onChange={handleChange}
              className="border rounded px-3 py-2 text-sm text-gray-700"
            />
          </div>
        ))}
      </div>

      {/* Proof document upload */}
      <div className="flex flex-col">
        <label className="text-sm font-semibold text-yellow-03 mb-2">
          {t('dashboard.proof_document')}
        </label>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept={IMAGE_OR_DOCUMENT_ACCEPT}
            id="document-proof-upload"
            className="hidden"
            onChange={onDocumentChange}
          />
          <label
            htmlFor="document-proof-upload"
            className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-lg cursor-pointer hover:bg-yellow-600"
          >
            <DocumentIcon className="w-5 h-5 mr-2" />
            {t('dashboard.upload_document')}
          </label>
          {documentProofName && (
            <span className="text-sm text-gray-700 truncate">
              {documentProofName}
            </span>
          )}
          {/* (Optional) If you store a URL, show current file */}
          {promoter?.documentProofUrl && (
            <a
              href={promoter.documentProofUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-blue-600 underline"
            >
              {t('dashboard.view_current')}
            </a>
          )}
        </div>
      </div>

      {/* Profile Info */}
      <div>
        <h3 className="text-lg font-semibold text-yellow-03 mb-2">
          {t('dashboard.about')}
        </h3>
        <textarea
          name="profileInfo"
          value={formData.profileInfo}
          onChange={handleChange}
          rows={4}
          className="w-full border rounded px-3 py-2 text-sm text-gray-700"
        />
      </div>

      <div className="pt-4 flex flex-col sm:flex-row justify-between gap-4">
        <button
          type="button"
          onClick={exitEditMode}
          disabled={saving}
          className="bg-gray-400 text-white font-semibold px-6 py-2 rounded hover:bg-gray-500 transition disabled:opacity-50"
        >
          {t('dashboard.cancel')}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="bg-yellow-500 text-white font-semibold px-6 py-2 rounded hover:bg-yellow-600 transition disabled:opacity-50"
        >
          {saving ? t('dashboard.saving') : t('dashboard.save_changes')}
        </button>
      </div>
    </form>
  );
};

export default PromoterInfoEdit;
